import {useCallback, useState} from 'react'
import {EditModeButtons} from '../../../calendar/components/EditModeButtons.tsx'
import {useConnections} from '../../../../providers/connections/useConnections.ts'
import type {UpdateConnectionRequest} from '@app/api-resolver/router'
import {FormFields} from './FormFields.tsx'
import {FormValues} from './FormValues.ts'
import type {ICalSubscriptionConnection} from '@package/shared'
import {useForm} from '@mantine/form'
import {zod4Resolver} from 'mantine-form-zod-resolver'

type Props = {
  connection: ICalSubscriptionConnection
  onSuccess: () => void
  disabled: boolean
}

export const UpdateForm = ({connection, onSuccess, disabled: containerDisabled}: Props) => {
  const {updateConnection, deleteConnection} = useConnections()
  const [updating, setUpdating] = useState<boolean>(false)
  const [deleting, setDeleting] = useState<boolean>(false)

  const form = useForm<FormValues>({
    initialValues: connection satisfies FormValues,
    validate: zod4Resolver(FormValues),
  })
  // update
  const handleSubmit = useCallback(
    async (formValues: FormValues) => {
      setUpdating(true)
      await updateConnection({
        ...connection,
        ...formValues,
      } satisfies UpdateConnectionRequest).finally(() => setUpdating(false))
      onSuccess()
    },
    [connection, onSuccess, updateConnection],
  )

  // delete
  const handleDelete = useCallback(async () => {
    setDeleting(true)
    await deleteConnection(connection.id).finally(() => setDeleting(false))
    onSuccess()
  }, [connection.id, deleteConnection, onSuccess])

  const disabled = containerDisabled

  return (
    <form onSubmit={form.onSubmit(handleSubmit)} style={{display: 'flex', flexDirection: 'column', gap: 10}}>
      <FormFields form={form} disabled={disabled} />
      <EditModeButtons
        submitVariant={'update'}
        submitting={updating}
        destructiveVariant={'delete'}
        onDestruct={handleDelete}
        destructing={deleting}
        withDestructiveLabel
        disabled={disabled}
      />
    </form>
  )
}
